import type { Router } from 'vue-router';
import { isAxiosError } from 'axios';
import posthog from 'posthog-js';
import { merchantInterface } from '@/api/merchants';
import { ServiceProviderUserRole } from '@/api/serviceProviders';
import { userInterface } from '@/api/users';
import { usesLocalFeatureFlags } from '@/composables/localFeatureFlags';
import useFeatureFlags, { FeatureFlag, FLAG_LOAD_TIMEOUT_MS } from '@/composables/useFeatureFlags';
import { parseMerchantSlug, toMerchantSlug, withMerchantSlug } from '@/helpers/merchantSlug';
import { isLoggingOut } from '@/state/authState';
import { useCurrentUserStore } from '@/stores/currentUser';
import { useMerchantsStore } from '@/stores/merchants';
import { useNarrativeStore } from '@/stores/narrative';

// A merchant slug that does not resolve fails the page the way a missing resource does.
// Falling back to the unscoped URL instead would load a page the user did not ask for,
// under whichever merchant happened to be selected.
const merchantLookupFailureStatus = (error: unknown): string => {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (status === 403 || status === 404) {
        return String(status);
    }

    return status === undefined ? '503' : '500';
};

// No layout has mounted when the partner guard runs, so nothing has identified the user to
// PostHog and its first flag load answered for an anonymous visitor. A partner without an
// account never reaches a layout that identifies them, so a flag targeted at users would stay
// off for them on every visit unless the guard identifies them and waits for the reload.
const loadFlagsForUser = async (user: userInterface): Promise<void> => {
    // Locally resolved flags do not depend on who PostHog thinks the visitor is.
    if (usesLocalFeatureFlags()) return;

    await useFeatureFlags().waitForFlagsSettled();

    const distinctId = user.id.toString();
    if (!posthog.__loaded || posthog.get_distinct_id() === distinctId) return;

    await new Promise<void>((resolve) => {
        const timeout = window.setTimeout(resolve, FLAG_LOAD_TIMEOUT_MS);
        // onFeatureFlags replays the anonymous flags straight away; only a load after identify counts.
        let identified = false;
        const stopListening = posthog.onFeatureFlags(() => {
            if (!identified) return;

            window.clearTimeout(timeout);
            stopListening();
            resolve();
        });

        identified = true;
        posthog.identify(distinctId, { id: user.id, name: user.name, email: user.email, createdAt: user.createdAt });
    });
};

export function setupGuards(router: Router): void {
    // A merchant lookup can outlast its navigation. Numbering navigations lets a superseded
    // one bail out instead of selecting its merchant over the page the user moved on to.
    let latestNavigation = 0;

    router.beforeEach(async (to) => {
        const navigation = ++latestNavigation;
        const narrativeStore = useNarrativeStore();
        const merchantsStore = useMerchantsStore();
        const currentUserStore = useCurrentUserStore();
        const requiresAccount = to.matched.some(record => record.meta.requiresAuth === true);
        const requiresPartner = to.matched.some(record => record.meta.requiresPartner === true);
        const requiresDeveloper = to.matched.some(record => record.meta.requiresDeveloper === true);

        // Platform tools authenticate the user independently of any narrated account.
        if (requiresPartner || (requiresDeveloper && !requiresAccount)) {
            try {
                await currentUserStore.loadCurrentUser();
                if (currentUserStore.currentUser) {
                    await currentUserStore.loadUserRoles();
                }
            } catch (error) {
                if (!isAxiosError(error)) throw error;

                const status = error.response?.status;
                if (status === 401 || status === 419) {
                    return { name: 'login', query: { redirect: to.fullPath } };
                }

                return { name: 'error', query: { status: status === undefined ? '503' : '500' } };
            }

            const user = currentUserStore.currentUser;
            if (!user) {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            if (requiresPartner) {
                if (!currentUserStore.userCanManagePartnerApps()) {
                    return { name: 'error', query: { status: '403' } };
                }

                await loadFlagsForUser(user);
                if (useFeatureFlags().isEnabled(FeatureFlag.PlatformOauthManagement) !== true) {
                    return { name: 'error', query: { status: '404' } };
                }
            }
        }

        // Load bootstrap data if not yet loaded (for authenticated routes)
        if (requiresAccount) {
            try {
                await narrativeStore.loadNarrative();
            } catch {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            // If narrative has no service provider, user is likely not authenticated
            if (!narrativeStore.currentServiceProvider) {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            try {
                await currentUserStore.loadCurrentUser();
                await currentUserStore.loadUserRoles();
            } catch {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            if (!currentUserStore.currentUser) {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            if (!merchantsStore.loaded) {
                await merchantsStore.fetchMerchants();
            }

            const merchantSlugParam = to.params.merchantSlug;
            const merchantSlug = Array.isArray(merchantSlugParam) ? merchantSlugParam[0] : merchantSlugParam;
            const hasMerchantSlug = merchantSlug != null && merchantSlug !== '';
            const isResourceRoute = to.matched.some(record => record.meta.merchantLocked === true);

            // A manual lock holds everywhere except a single resource's page, which only
            // loads under the merchant that owns it. Anywhere else the locked selection is
            // restored, and a slug naming a different merchant is swapped for the locked one.
            if (narrativeStore.getSelectedMerchantLocked && !isResourceRoute) {
                narrativeStore.restoreLockedMerchant();

                const lockedMerchant = narrativeStore.currentMerchant;
                const lockedMerchantSlug = lockedMerchant === undefined ? undefined : toMerchantSlug(lockedMerchant.uuid);
                if (hasMerchantSlug && merchantSlug !== lockedMerchantSlug) {
                    return withMerchantSlug(to.fullPath, lockedMerchantSlug);
                }
            } else if (hasMerchantSlug) {
                let merchant: merchantInterface;
                try {
                    merchant = await merchantsStore.fetchMerchantByUuid(parseMerchantSlug(merchantSlug));
                } catch (error) {
                    return navigation === latestNavigation
                        ? { name: 'error', query: { status: merchantLookupFailureStatus(error) } }
                        : false;
                }

                if (navigation !== latestNavigation) {
                    return false;
                }

                narrativeStore.setCurrentMerchantFromRoute(merchant);
            }

            // A merchant user without a selected merchant sends every request without a
            // merchant header, so the page loads but its endpoints refuse it. Deep links
            // reach any authed route directly, so the account selector has to gate all of
            // them, not just the dashboard. Staff narrate a merchant only when they choose
            // to, and the two account routes exist to get a merchant in the first place.
            if (
                !currentUserStore.userHasRoleOnCurrentServiceProvider()
                && !narrativeStore.currentMerchant
                && to.name !== 'select-account'
                && to.name !== 'register-merchant'
            ) {
                return { name: 'select-account', query: { redirect: to.fullPath } };
            }
        }

        if ((to.name === 'dashboard' || to.name === 'merchant.dashboard') && currentUserStore.userHasRoleOnCurrentServiceProvider()) {
            return { name: 'staff.dashboard' };
        }

        if (to.name === 'dashboard' && narrativeStore.currentMerchant) {
            return { name: 'merchant.dashboard', params: { merchantSlug: toMerchantSlug(narrativeStore.currentMerchant.uuid) } };
        }

        // Staff guard
        if (to.matched.some(record => record.meta.requiresStaff === true)) {
            if (!currentUserStore.userHasRoleOnCurrentServiceProvider()) {
                return { name: 'dashboard' };
            }
        }

        // Developer guard
        if (requiresDeveloper) {
            if (!currentUserStore.userIsDeveloper()) {
                if (!requiresAccount) {
                    return { name: 'error', query: { status: '403' } };
                }

                return { name: 'dashboard' };
            }
        }

        // Checked after the developer guard so a refused visitor never waits on PostHog.
        if (to.matched.some(record => record.meta.requiresPlatformOauthManagement === true)) {
            const user = currentUserStore.currentUser;
            if (!user) {
                return { name: 'login', query: { redirect: to.fullPath } };
            }

            await loadFlagsForUser(user);
            if (useFeatureFlags().isEnabled(FeatureFlag.PlatformOauthManagement) !== true) {
                return { name: 'error', query: { status: '404' } };
            }
        }

        // Service Provider Owner guard
        if (to.matched.some(record => record.meta.requiresOwner === true)) {
            if (!currentUserStore.userHasRoleOnCurrentServiceProvider(ServiceProviderUserRole.Owner)) {
                return { name: 'dashboard' };
            }
        }

        // Guest guard - redirect authenticated users away from welcome/login/register.
        // Only these redirect checks need the service provider, so loadNarrative is
        // scoped to them: other guest routes (terms, register-company) would otherwise
        // fire a redundant /api/narrative/serviceProvider request that the layout
        // already makes.
        // Skip if we're in the middle of a logout — the user should reach the login page
        if (to.matched.some(record => record.meta.guest === true) && to.meta.requiresAuth !== true) {
            if (!isLoggingOut.value && (to.name === 'welcome' || to.name === 'login' || to.name === 'register')) {
                try {
                    await narrativeStore.loadNarrative();
                } catch {
                    // Not authenticated, continue to guest page
                }

                if (narrativeStore.currentServiceProvider) {
                    try {
                        await currentUserStore.loadCurrentUser();
                        if (currentUserStore.currentUser) {
                            return { name: 'dashboard' };
                        }
                    } catch {
                        // Not authenticated, continue to guest page
                    }
                }
            }
        }
    });
}
